import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

/**
 * `size` exists because five editor-toolbar fields (link URL, image src, alt text, find,
 * replace) hand-rolled a shorter control — `px-2 py-1 text-xs` on a raw `<input>` — rather
 * than take `Input` and its `h-9`, which is genuinely too tall for a dense toolbar row. The
 * need was real; the fork was not. `sm` is that control, owned here, so the toolbar and any
 * future compact row share one definition instead of copying a className.
 *
 * Note `size` collides with the native `<input size>` attribute (a character-width hint),
 * which is why it is omitted from the extended props below. Nothing in this app used it.
 */
// Not exported: nothing outside this file composes the class string, and exporting it would
// cost the file its Fast Refresh boundary (react-refresh/only-export-components).
const inputVariants = cva(
  cn(
    'flex w-full rounded bg-surface-elevated text-text transition-colors',
    // Resting boundary (Item A, human ruling 2026-09-24): the system's own `input-border` token,
    // the one Checkbox and RadioGroup already draw. Focus turns the edge primary and thickens it
    // with a 1px ring, so it stays distinguishable from rest; invalid turns it danger.
    'border border-input-border',
    'file:border-0 file:bg-transparent file:font-medium',
    'placeholder:text-text-subtle',
    'focus-visible:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'aria-[invalid=true]:border-danger',
  ),
  {
    variants: {
      size: {
        default: 'h-9 px-3 py-1 text-sm file:text-sm',
        sm: 'h-7 px-2 py-0.5 text-body-sm file:text-body-sm',
      },
    },
    defaultVariants: { size: 'default' },
  },
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ size }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }
